// config/protocolRulesetAdapter.ts
/**
 * Converts the server protocol ruleset (types/protocolRuleset.ts, snake_case,
 * day-based) into the client ProtocolConfig shape consumed by bundleUtils and
 * the Bundle Status Report.
 *
 * The server ruleset is the single source of truth — there is no static
 * client-side protocol config. Until the ruleset is fetched, the context
 * config is null and consumers render loading states.
 */

import {
  BundleCollectionAnchor,
  BundleMilestone,
  ExpectedGap,
  ProtocolConfig,
} from '../types/protocolConfig';
import {
  ProtocolConfigResponse,
  ProtocolRulesetExpectedGap,
  ProtocolRulesetMilestone,
} from '../types/protocolRuleset';

// Safety nets if a ruleset omits the report alert thresholds.
const DEFAULT_STALE_DAYS = 21;
const DEFAULT_EXPIRING_WARNING_DAYS = 4;

const GAP_REASONS: ExpectedGap['reason'][] = [
  'primary_endpoint',
  'year_transition',
  'washout',
  'other',
];

const toGapReason = (reason: string | undefined): ExpectedGap['reason'] =>
  GAP_REASONS.find(r => r === reason) ?? 'other';

const toCollectionAnchor = (
  anchor: ProtocolRulesetMilestone['collection_anchor']
): BundleCollectionAnchor | undefined => {
  if (anchor === 'target_day') return 'expected_day';
  if (anchor === 'first_test') return 'first_test';
  return undefined;
};

const toMilestone = (
  ms: ProtocolRulesetMilestone,
  yearBoundaries: number[]
): BundleMilestone => {
  const isPreAnchor = ms.window_mode === 'pre_anchor';
  // Pre-anchor milestones carry no target_day; pin them to protocol Day 1
  // (the anchor) for ordering/positioning. Their window math is driven by
  // windowMode, not by these values.
  const expectedDay = ms.target_day ?? 1;
  const expectedWeek = Math.floor((expectedDay - 1) / 7);
  const collectionAnchor = toCollectionAnchor(ms.collection_anchor);
  let studyYear = yearBoundaries.length + 1;
  for (let i = 0; i < yearBoundaries.length; i++) {
    if (expectedWeek <= yearBoundaries[i]) {
      studyYear = i + 1;
      break;
    }
  }
  return {
    bundleNumber: ms.bundle_number,
    expectedWeek,
    expectedDay,
    ...(ms.window_days_before !== undefined ? { windowDaysBefore: ms.window_days_before } : {}),
    ...(ms.window_days_after !== undefined ? { windowDaysAfter: ms.window_days_after } : {}),
    ...(collectionAnchor ? { collectionAnchor } : {}),
    ...(isPreAnchor ? { windowMode: 'pre_anchor' as const } : {}),
    ...(ms.target_sessions !== undefined ? { targetSessions: ms.target_sessions } : {}),
    ...(ms.minimum_reliable_sessions !== undefined ? { minimumValidTests: ms.minimum_reliable_sessions } : {}),
    studyYear,
    reminderVisitId: ms.visit_id,
    label: ms.label,
    ...(ms.visit_label ? { visitName: ms.visit_label } : {}),
  };
};

const toExpectedGap = (gap: ProtocolRulesetExpectedGap): ExpectedGap => ({
  gapId: gap.gap_id,
  description: gap.description ?? '',
  startWeek: gap.start_week,
  endWeek: gap.end_week,
  reason: toGapReason(gap.reason),
  requiredBundlesBeforeGap: gap.required_bundles_before_gap,
});

export const protocolConfigFromRuleset = (
  response: ProtocolConfigResponse
): ProtocolConfig => {
  const ruleset = response.ruleset;
  const rb = ruleset.bundles;

  const lastMilestoneWeek = rb.milestones.length
    ? Math.floor(((rb.milestones[rb.milestones.length - 1].target_day ?? 1) - 1) / 7)
    : 0;
  const totalWeeks = ruleset.timeline?.total_weeks ?? lastMilestoneWeek + 4;
  const yearBoundaries = ruleset.timeline?.year_boundaries?.length
    ? ruleset.timeline.year_boundaries
    : [totalWeeks];

  const milestones = rb.milestones.map(ms => toMilestone(ms, yearBoundaries));

  return {
    metadata: {
      studyName: ruleset.study_label ?? response.study_label ?? ruleset.study_key,
      protocolId: ruleset.protocol_id ?? response.study_protocol ?? ruleset.ruleset_key,
      ...((response.ruleset_status ?? ruleset.status)
        ? { rulesetStatus: (response.ruleset_status ?? ruleset.status) as string }
        : {}),
      ...(ruleset.anchor?.label ? { anchorLabel: ruleset.anchor.label } : {}),
    },
    timeline: {
      totalWeeks,
      yearBoundaries,
    },
    bundles: {
      targetTestsPerBundle: rb.target_sessions,
      minimumValidTests: rb.minimum_reliable_sessions,
      windowDays: rb.collection_window_days,
      // Client schedule math anchors on the participant's Rand Date field;
      // 'actual_baseline' is only used if a ruleset ever declares it.
      scheduleAnchor: ruleset.anchor?.type === 'actual_baseline' ? 'actual_baseline' : 'rand_date',
      defaultWindowDaysBefore: rb.default_window_days_before,
      defaultWindowDaysAfter: rb.default_window_days_after,
      totalExpectedBundles: rb.total_expected_bundles ?? milestones.length,
      milestones,
      expectedGaps: (rb.expected_gaps ?? []).map(toExpectedGap),
      completionTiers: {
        complete: rb.target_sessions,
        partialMin: rb.minimum_reliable_sessions,
      },
    },
    alerts: {
      staleDays: ruleset.alerts?.stale_days ?? DEFAULT_STALE_DAYS,
      expiringWarningDays: ruleset.alerts?.expiring_warning_days ?? DEFAULT_EXPIRING_WARNING_DAYS,
    },
    ...(ruleset.cohorts
      ? { cohorts: { ...(ruleset.cohorts.b_start_date ? { bStartDate: ruleset.cohorts.b_start_date } : {}) } }
      : {}),
  };
};

export default protocolConfigFromRuleset;
